import * as React from 'react';

import { CaretSortIcon, CheckIcon, Cross2Icon } from '@radix-ui/react-icons';
import { cn } from 'utils/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'components/ui/command';
import { FormItem, FormMessage } from 'components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Label } from 'components/ui/label';
import { ScrollArea } from 'components/ui/scroll-area';

interface Option {
  name: string;
  value: string;
}

interface SelectBoxProps {
  label?: string;
  options: Option[];
  value?: string[] | string;
  onChange?: (values: string[] | string) => void;
  placeholder?: string;
  inputPlaceholder?: string;
  emptyPlaceholder?: string;
  className?: string;
  multiple?: boolean;
  multipleItemsAppendLabel?: string;
}

const ComboboxMultiGs = React.forwardRef<HTMLInputElement, SelectBoxProps>(
  (
    {
      label,
      inputPlaceholder,
      emptyPlaceholder,
      placeholder,
      className,
      options,
      value,
      onChange,
      multiple,
      multipleItemsAppendLabel = 'clientes selecionados',
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState<string>('');

    const handleSelect = (selectedValue: string) => {
      if (multiple) {
        const newValue =
          value?.includes(selectedValue) && Array.isArray(value)
            ? value.filter((v) => v !== selectedValue)
            : [...(value ?? []), selectedValue];
        onChange?.(newValue);
      } else {
        onChange?.(selectedValue);
        setIsOpen(false);
      }
    };

    const handleClear = () => {
      onChange?.(multiple ? [] : '');
    };

    return (
      <FormItem>
        <div className="grid w-full items-center gap-1.5">
          {label && (
            <Label className="font-GilroySemibold text-sm text-slate-800">
              {label}
            </Label>
          )}
          <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
              <div
                className={cn(
                  'whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border bg-background hover:bg-accent hover:text-accent-foreground px-4 py-2 flex h-10 text-wrap items-center text-left font-sans justify-between border-solid border-slate-200 focus-visible:ring-primary-500 overflow-hidden',
                  !value && 'text-muted-foreground'
                )}
              >
                <div
                  className={cn(
                    'items-center gap-1 overflow-hidden text-sm',
                    multiple
                      ? 'flex flex-grow flex-wrap '
                      : 'inline-flex whitespace-nowrap'
                  )}
                >
                  {value && value.length > 0 ? (
                    multiple ? (
                      Array.isArray(value) && value.length > 1 ? (
                        <span className="text-sm text-muted-foreground">
                          {value.length} {multipleItemsAppendLabel}
                        </span>
                      ) : (
                        options
                          .filter(
                            (option) =>
                              Array.isArray(value) &&
                              value.includes(option.value)
                          )
                          .map((option) => (
                            <span
                              key={option.value}
                              className="inline-flex items-center gap-1 rounded-md border py-0.5 pl-2 pr-1 text-xs font-medium text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                            >
                              <span>{option.name}</span>
                              <span
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleSelect(option.value);
                                }}
                                className="flex items-center rounded-sm px-[1px] text-muted-foreground/60 hover:bg-accent hover:text-muted-foreground"
                              >
                                <Cross2Icon />
                              </span>
                            </span>
                          ))
                      )
                    ) : (
                      <span className="text-ellipsis overflow-hidden">
                        {options.find((opt) => opt.value === value)?.name}
                      </span>
                    )
                  ) : (
                    <span className="mr-auto text-muted-foreground">
                      {placeholder}
                    </span>
                  )}
                </div>
                <div className="flex items-center self-stretch pl-1 text-muted-foreground/60 hover:text-foreground [&>div]:flex [&>div]:items-center [&>div]:self-stretch">
                  {value && value.length > 0 ? (
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        handleClear();
                      }}
                    >
                      <Cross2Icon className="size-4" />
                    </div>
                  ) : (
                    <div>
                      <CaretSortIcon className="size-4" />
                    </div>
                  )}
                </div>
              </div>
            </PopoverTrigger>
            <PopoverContent
              className="w-[var(--radix-popover-trigger-width)] p-0"
              align="start"
            >
              <Command
                filter={(value, search) => {
                  if (value.toLowerCase().includes(search.toLowerCase())) return 1;
                  return 0;
                }}
              >
                <div className="relative">
                  <CommandInput
                    value={searchTerm}
                    onValueChange={(e: any) => setSearchTerm(e)}
                    ref={ref}
                    placeholder={inputPlaceholder ?? 'Search...'}
                    className="h-9"
                  />
                  {searchTerm && (
                    <div
                      className="absolute inset-y-0 right-0 flex cursor-pointer items-center pr-3 text-muted-foreground hover:text-foreground"
                      onClick={() => setSearchTerm('')}
                    >
                      <Cross2Icon className="size-4" />
                    </div>
                  )}
                </div>
                <CommandEmpty>
                  {emptyPlaceholder ?? 'No results found.'}
                </CommandEmpty>
                <CommandGroup>
                  <CommandList>
                    <ScrollArea>
                      <div className="max-h-64">
                        {options.map((option) => {
                          const isSelected =
                            Array.isArray(value) &&
                            value.includes(option.value);

                          return (
                            <CommandItem
                              key={option.value}
                              value={option.name}
                              onSelect={() => handleSelect(option.value)}
                            >
                              {multiple && (
                                <div
                                  className={cn(
                                    'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                                    isSelected
                                      ? 'bg-primary text-primary-foreground'
                                      : 'opacity-50 [&_svg]:invisible'
                                  )}
                                >
                                  <CheckIcon />
                                </div>
                              )}

                              <span>{option.name}</span>

                              {!multiple && option.value === value && (
                                <CheckIcon
                                  className={cn(
                                    'ml-auto',
                                    option.value === value
                                      ? 'opacity-100'
                                      : 'opacity-0'
                                  )}
                                />
                              )}
                            </CommandItem>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  </CommandList>
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>
          <FormMessage />
        </div>
      </FormItem>
    );
  }
);

ComboboxMultiGs.displayName = 'ComboboxMultiGs';

export { ComboboxMultiGs };